import { useEffect, useState } from 'react';
import type { DeskTicketRule } from '@detect/shared';
import { useTenant } from '../../contexts/TenantContext';
import { clientService } from '../../services/clients';
import { ruleService } from '../../services/alerting';
import type { ListKey } from './ticketRules';

/**
 * Which alerts become Desk TICKETS (#19). Alerts Hub is the default for everything else.
 * Granular on purpose: each rule selects alerts by sensor, datapoint, severity, client,
 * resource, the alert rule that routed it, kind of detection and kind of condition — all
 * of a rule's filters must match. "Every alert" is a deliberate, explicit choice.
 * The first matching rule wins. Desk may still veto a ticket (the alert then stays in
 * the hub, and the alert's notes say so).
 */

const SEVERITIES = ['warning', 'error', 'critical'] as const;
const MODES = ['outage', 'change-expected', 'change-escaped', 'change-unrecovered'] as const;
const CONDITIONS = ['threshold', 'no-data'] as const;

type Match = DeskTicketRule['match'];

const csv = (text: string): string[] => [...new Set(text.split(/[\s,]+/).map((x) => x.trim()).filter((x) => x !== ''))];

export function TicketRulesEditor({ rules, onChange }: { rules: DeskTicketRule[]; onChange: (next: DeskTicketRule[]) => void }) {
  const { tenantId } = useTenant();
  const [clients, setClients] = useState<{ id: string; name: string }[]>([]);
  const [alertRules, setAlertRules] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    if (!tenantId) return;
    let live = true;
    void allPages((startAfter) => clientService.list(tenantId, { limit: 100, startAfter }))
      .then((items) => live && setClients(items.map((c) => ({ id: c.id, name: c.name }))))
      .catch(() => undefined);
    void allPages((startAfter) => ruleService.list(tenantId, { limit: 100, startAfter }))
      .then((items) => live && setAlertRules(items.map((r) => ({ id: r.id, name: r.name }))))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [tenantId]);

  const update = (i: number, patch: Partial<DeskTicketRule>) => onChange(rules.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  const setMatch = (i: number, key: ListKey, values: string[]) => {
    const next: Record<string, unknown> = { ...rules[i].match };
    delete next.all;
    if (values.length === 0) delete next[key];
    else next[key] = values;
    update(i, { match: next as Match });
  };
  const toggle = (i: number, key: ListKey, value: string, on: boolean) => {
    const cur = (rules[i].match[key] as string[] | undefined) ?? [];
    setMatch(i, key, on ? [...cur, value] : cur.filter((v) => v !== value));
  };
  const add = () => {
    let n = rules.length + 1;
    while (rules.some((r) => r.id === `rule-${n}`)) n++;
    onChange([...rules, { id: `rule-${n}`, name: '', enabled: true, match: { severities: ['critical'] } } as DeskTicketRule]);
  };

  return (
    <fieldset className="form-field" aria-label="Ticket rules">
      <legend>Tickets instead of the Alerts Hub</legend>
      <p className="text-muted">
        By default every alert goes to Desk&apos;s Alerts Hub. Add a rule to send a <strong>narrow</strong> class of alerts to Desk as a ticket
        instead. An alert that has already landed somewhere stays there; Desk may still decline a ticket, and the alert&apos;s notes will say so.
      </p>
      {rules.length === 0 && <p className="text-muted">No rules: everything goes to the Alerts Hub.</p>}
      {rules.map((r, i) => {
        const all = r.match.all === true;
        return (
          <div key={r.id} className="card" role="group" aria-label={`Ticket rule ${i + 1}`}>
            <div className="form-field">
              <label htmlFor={`tr-name-${i}`}>Rule name</label>
              <input id={`tr-name-${i}`} value={r.name} onChange={(e) => update(i, { name: e.target.value })} />
            </div>
            <label className="checkbox-label">
              <input type="checkbox" checked={r.enabled} onChange={(e) => update(i, { enabled: e.target.checked })} />
              Enabled
            </label>
            <label className="checkbox-label">
              <input type="checkbox" checked={all} onChange={(e) => update(i, { match: e.target.checked ? { all: true } : ({ severities: ['critical'] } as Match) })} />
              Every alert (no filters)
            </label>
            {all ? (
              <p className="notice">Every alert will be filed as a ticket. Prefer a narrower rule.</p>
            ) : (
              <>
                <CsvField id={`tr-sensors-${i}`} label="Sensor keys (comma separated)" values={r.match.sensorKeys ?? []} onChange={(v) => setMatch(i, 'sensorKeys', v)} />
                <CsvField id={`tr-res-${i}`} label="Resource ids (comma separated)" values={r.match.resourceIds ?? []} onChange={(v) => setMatch(i, 'resourceIds', v)} />
                <CsvField id={`tr-dps-${i}`} label="Datapoints (comma separated)" values={r.match.datapoints ?? []} onChange={(v) => setMatch(i, 'datapoints', v)} />
                <fieldset className="form-field">
                  <legend>Severity</legend>
                  {SEVERITIES.map((s) => (
                    <label key={s} className="checkbox-label">
                      <input type="checkbox" checked={r.match.severities?.includes(s) ?? false} onChange={(e) => toggle(i, 'severities', s, e.target.checked)} />
                      {s}
                    </label>
                  ))}
                </fieldset>
                <fieldset className="form-field">
                  <legend>Kind of condition</legend>
                  {CONDITIONS.map((c) => (
                    <label key={c} className="checkbox-label">
                      <input type="checkbox" checked={r.match.conditionKinds?.includes(c) ?? false} onChange={(e) => toggle(i, 'conditionKinds', c, e.target.checked)} />
                      {c === 'no-data' ? 'no data' : 'threshold breach'}
                    </label>
                  ))}
                </fieldset>
                <fieldset className="form-field">
                  <legend>Kind of detection</legend>
                  {MODES.map((m) => (
                    <label key={m} className="checkbox-label">
                      <input type="checkbox" checked={r.match.detectionModes?.includes(m) ?? false} onChange={(e) => toggle(i, 'detectionModes', m, e.target.checked)} />
                      {m}
                    </label>
                  ))}
                </fieldset>
                {clients.length > 0 && (
                  <fieldset className="form-field">
                    <legend>Clients</legend>
                    {clients.map((c) => (
                      <label key={c.id} className="checkbox-label">
                        <input type="checkbox" checked={r.match.clientIds?.includes(c.id) ?? false} onChange={(e) => toggle(i, 'clientIds', c.id, e.target.checked)} />
                        {c.name}
                      </label>
                    ))}
                  </fieldset>
                )}
                {alertRules.length > 0 && (
                  <fieldset className="form-field">
                    <legend>Routed by alert rule</legend>
                    {alertRules.map((a) => (
                      <label key={a.id} className="checkbox-label">
                        <input type="checkbox" checked={r.match.alertRuleIds?.includes(a.id) ?? false} onChange={(e) => toggle(i, 'alertRuleIds', a.id, e.target.checked)} />
                        {a.name}
                      </label>
                    ))}
                  </fieldset>
                )}
                <p className="field-hint">All the filters you set must match.</p>
              </>
            )}
            <div className="row-actions">
              <button type="button" className="btn btn-ghost" disabled={i === 0} onClick={() => onChange(move(rules, i, i - 1))}>Move up</button>
              <button type="button" className="btn btn-ghost" disabled={i === rules.length - 1} onClick={() => onChange(move(rules, i, i + 1))}>Move down</button>
              <button type="button" className="btn btn-ghost" onClick={() => onChange(rules.filter((_, k) => k !== i))}>Remove rule</button>
            </div>
          </div>
        );
      })}
      <button type="button" className="btn btn-secondary" onClick={add}>Add ticket rule</button>
    </fieldset>
  );
}

/** Every page of a cursor list, up to a sane bound (5 × 100). */
async function allPages<T extends { id: string }>(
  fetchPage: (startAfter: string | undefined) => Promise<{ items: T[]; pagination: { hasMore?: boolean; nextCursor?: string | null } | null }>
): Promise<T[]> {
  const out: T[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < 5; page++) {
    const res = await fetchPage(cursor);
    out.push(...res.items);
    if (!res.pagination?.hasMore || !res.pagination.nextCursor) break;
    cursor = res.pagination.nextCursor;
  }
  return out;
}

/** A comma-separated list input that keeps what the user is TYPING (a trailing comma must survive the keystroke). */
function CsvField({ id, label, values, onChange }: { id: string; label: string; values: string[]; onChange: (v: string[]) => void }) {
  const [text, setText] = useState(values.join(', '));
  return (
    <div className="form-field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          onChange(csv(e.target.value));
        }}
      />
    </div>
  );
}

function move<T>(list: readonly T[], from: number, to: number): T[] {
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}
